import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './input';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { MapPin, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getGeocodingSuggestions } from '@/lib/mapbox';

export interface PlaceData {
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  latitude: number;
  longitude: number;
}

interface LocationResult {
  id: string;
  city: string;
  state_code: string;
  county: string | null;
  lat: number | null;
  lon: number | null;
  display_label: string;
  similarity_score: number;
  source: string;
}

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string, placeData?: PlaceData) => void;
  placeholder?: string;
  className?: string;
  error?: string;
}

export const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "City, ST or ZIP code",
  className,
  error,
}) => {
  const [suggestions, setSuggestions] = useState<LocationResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const lastQueryRef = useRef<string>('');

  const searchLocal = useCallback(async (query: string): Promise<LocationResult[]> => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return [];

    try {
      const { data, error } = await supabase.rpc('search_locations', {
        q: trimmed,
        lim: 20,
      });

      if (error) {
        console.error('Local search error:', error);
        return [];
      }

      return (data || []) as LocationResult[];
    } catch (err) {
      console.error('Local search exception:', err);
      return [];
    }
  }, []);

  const searchMapboxFallback = useCallback(async (query: string): Promise<LocationResult[]> => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return [];

    try {
      const data = await getGeocodingSuggestions(trimmed);
      const features = data?.features || [];

      const results: LocationResult[] = features.slice(0, 8).map((f: any) => {
        const [longitude, latitude] = f.center || [0, 0];
        const cityCtx = f.context?.find((c: any) => c.id?.startsWith('place'));
        const stateCtx = f.context?.find((c: any) => c.id?.startsWith('region'));

        const city = cityCtx?.text || f.text || '';
        const stateCode = (stateCtx?.short_code || stateCtx?.text || '').replace('US-', '');

        return {
          id: f.id || crypto.randomUUID(),
          city: city || f.text,
          state_code: stateCode,
          county: null,
          lat: latitude,
          lon: longitude,
          display_label: `${city || f.text}, ${stateCode}`,
          similarity_score: 1.0,
          source: 'mapbox_live',
        };
      });

      // Cache each result in background (fire-and-forget)
      for (const r of results) {
        if (r.city && r.state_code) {
          supabase.functions.invoke('geocode-cache-upsert', {
            body: {
              query: trimmed,
              place_name: r.display_label,
              city: r.city,
              state_code: r.state_code,
              lat: r.lat,
              lon: r.lon,
              mapbox_place_id: r.id,
            },
          }).catch(() => {}); // Silently fail
        }
      }

      return results;
    } catch (err) {
      console.error('Mapbox fallback error:', err);
      return [];
    }
  }, []);

  const performSearch = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoading(false);
      return;
    }

    lastQueryRef.current = query;
    setIsLoading(true);

    // Step 1: Local DB search
    const localResults = await searchLocal(trimmed);

    // Check if this is still the current query
    if (lastQueryRef.current !== query) {
      return;
    }

    if (localResults.length > 0) {
      setSuggestions(localResults);
      setShowSuggestions(true);
      setSelectedIndex(-1);
      setIsLoading(false);
      return;
    }

    // Step 2: Mapbox fallback (only if local returned nothing)
    try {
      const mapboxResults = await searchMapboxFallback(trimmed);

      if (lastQueryRef.current !== query) {
        return;
      }

      setSuggestions(mapboxResults);
      setShowSuggestions(mapboxResults.length > 0);
      setSelectedIndex(-1);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [searchLocal, searchMapboxFallback]);

  const debouncedSearch = useCallback(
    (query: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        performSearch(query);
      }, 200);
    },
    [performSearch]
  );

  // Position dropdown — prefers DOWNWARD, clamps to the visual viewport so the
  // menu is always fully on-screen (including with the iOS keyboard open) and
  // never pushes the document horizontally.
  const updateMenuPosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vv = (typeof window !== 'undefined' && window.visualViewport) || null;
    const vpTop = vv ? vv.offsetTop : 0;
    const vpLeft = vv ? vv.offsetLeft : 0;
    const vpWidth = vv ? vv.width : window.innerWidth;
    const vpHeight = vv ? vv.height : window.innerHeight;

    const GAP = 4;
    const MARGIN = 8;
    const spaceBelow = vpTop + vpHeight - rect.bottom - GAP - MARGIN;
    const spaceAbove = rect.top - vpTop - GAP - MARGIN;
    // Only flip up when there's clearly not enough room below.
    const flipUp = spaceBelow < 160 && spaceAbove > spaceBelow;
    const available = Math.max(120, flipUp ? spaceAbove : spaceBelow);

    const width = Math.min(rect.width, vpWidth - MARGIN * 2);
    const left = Math.min(
      Math.max(rect.left, vpLeft + MARGIN),
      vpLeft + vpWidth - width - MARGIN,
    );

    setMenuStyle({
      position: 'fixed',
      top: flipUp ? undefined : rect.bottom + GAP,
      bottom: flipUp ? Math.max(MARGIN, vpTop + vpHeight - rect.top + GAP) : undefined,
      left,
      width,
      maxHeight: Math.min(available, Math.round(vpHeight * 0.6)),
      zIndex: 10003,
    });
  }, []);


  useEffect(() => {
    if (showSuggestions) updateMenuPosition();
  }, [showSuggestions, updateMenuPosition, value, suggestions.length]);

  useEffect(() => {
    if (!showSuggestions) return;
    const handler = () => updateMenuPosition();
    window.addEventListener('resize', handler);
    document.addEventListener('scroll', handler, true);
    // iOS keyboard show/hide changes the visual viewport, not window size.
    const vv = window.visualViewport;
    vv?.addEventListener('resize', handler);
    vv?.addEventListener('scroll', handler);
    return () => {
      window.removeEventListener('resize', handler);
      document.removeEventListener('scroll', handler, true);
      vv?.removeEventListener('resize', handler);
      vv?.removeEventListener('scroll', handler);
    };
  }, [showSuggestions, updateMenuPosition]);


  const handleSelectPlace = (result: LocationResult) => {
    // Cancel any pending debounced search to prevent it from overwriting
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    const canonicalLabel = `${result.city}, ${result.state_code}`;
    const placeData: PlaceData = {
      name: canonicalLabel,
      address: canonicalLabel,
      city: result.city,
      state: result.state_code,
      country: 'US',
      postal_code: '',
      latitude: result.lat || 0,
      longitude: result.lon || 0,
    };
    
    // Update parent with the canonical "City, ST" label
    onChange(canonicalLabel, placeData);
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setIsLoading(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (newValue.trim().length >= 2) {
      setIsLoading(true);
      debouncedSearch(newValue);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelectPlace(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as Node;
    if (menuRef.current && relatedTarget && menuRef.current.contains(relatedTarget)) {
      return;
    }
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={() => {
            if (value && suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          className={cn('pr-8 text-ellipsis overflow-hidden', error && 'border-destructive', className)}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none z-10">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <MapPin className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && menuStyle &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            role="listbox"
            className="bg-popover border border-border rounded-md shadow-lg overflow-y-auto overscroll-contain pointer-events-auto touch-pan-y z-[10003]"
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.id}
                type="button"
                role="option"
                aria-selected={selectedIndex === index}
                onMouseDown={(e) => e.preventDefault()}
                className={cn(
                  'w-full px-3 py-2 text-left text-sm transition-colors focus:outline-none focus:bg-muted focus:text-foreground focus-visible:ring-1 focus-visible:ring-ring',
                  'hover:bg-muted hover:text-foreground',
                  'flex items-start gap-2',
                  selectedIndex === index && 'bg-brand-teal-subtle text-foreground'
                )}
                onClick={() => handleSelectPlace(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {suggestion.city}, {suggestion.state_code}
                  </div>
                  {suggestion.county && (
                    <div className="text-xs text-muted-foreground">{suggestion.county}</div>
                  )}
                </div>
              </button>
            ))}
          </div>,
          document.body
        )}

      {error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
